import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';

export const checkCouponAvailability = createServerFn({ method: 'GET' })
  .validator(z.object({}).optional())
  .handler(async () => {
    const { קופוניםBoard } = await import('@api/BoardSDK');
    const board = new קופוניםBoard();
    const result = await board
      .items()
      .withColumns(['column_'])
      .where({ column: 'זמין' })
      .withPagination({ limit: 1 })
      .execute();

    return { available: result.items.length > 0 };
  });

export const getCoupons = createServerFn({ method: 'GET' })
  .validator(
    z
      .object({
        status: z.string().optional(),
        search: z.string().optional(),
        limit: z.number().optional(),
        cursor: z.string().optional(),
      })
      .optional()
  )
  .handler(async ({ data: _data }) => {
    const { קופוניםBoard } = await import('@api/BoardSDK');
    const board = new קופוניםBoard();
    const limit = _data?.limit || 50;

    let query = board
      .items()
      .withColumns(['column_', 'column', 'column__', 'column_1', 'column_2', 'column_3'])
      .orderBy({ column: 'createdAt', direction: 'desc' });

    if (_data?.status) {
      query = query.where({ column: _data.status });
    }

    if (_data?.search) {
      query = query.where({ column_: { contains: _data.search } });
    }

    query = query.withPagination({ limit, cursor: _data?.cursor });

    const result = await query.execute();
    return result;
  });

export const createCoupons = createServerFn({ method: 'POST' })
  .validator(
    z.object({
      count: z.number(),
      prefix: z.string().optional(),
    })
  )
  .handler(async ({ data }) => {
    const { קופוניםBoard } = await import('@api/BoardSDK');
    const board = new קופוניםBoard();
    const prefix = data.prefix || 'KS';
    let created = 0;

    for (let i = 0; i < data.count; i++) {
      const code = `${prefix}-${Date.now()}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
      await board
        .item()
        .create({
          name: code,
          column_: code,
          column: 'זמין',
        })
        .execute();
      created++;
    }

    return { success: true, count: created };
  });

export const importCouponCodes = createServerFn({ method: 'POST' })
  .validator(
    z.object({
      codes: z.array(z.string()),
    })
  )
  .handler(async ({ data }) => {
    const { קופוניםBoard } = await import('@api/BoardSDK');
    const board = new קופוניםBoard();
    let imported = 0;

    for (const code of data.codes) {
      try {
        await board
          .item()
          .create({
            name: code,
            column_: code,
            column: 'זמין',
          })
          .execute();
        imported++;
      } catch (err) {
        console.error('Failed to import coupon code:', code, err);
      }
    }

    return { success: true, imported, total: data.codes.length };
  });

export const assignCoupons = createServerFn({ method: 'POST' })
  .validator(
    z.object({
      registrationId: z.string(),
      idNumbers: z.array(z.string()),
    })
  )
  .handler(async ({ data }) => {
    const { קופוניםBoard, רישומיםBoard } = await import('@api/BoardSDK');
    const couponsBoard = new קופוניםBoard();
    const registrationsBoard = new רישומיםBoard();

    const assignedCoupons: Array<{ idNumber: string; couponCode: string }> = [];

    for (const idNumber of data.idNumbers) {
      const availableCoupons = await couponsBoard
        .items()
        .withColumns(['column_', 'column'])
        .where({ column: 'זמין' })
        .withPagination({ limit: 1 })
        .execute();

      if (availableCoupons.items.length === 0) {
        throw new Error('אין קופונים זמינים');
      }

      const coupon = availableCoupons.items[0];

      await couponsBoard
        .item(coupon.id)
        .update({
          column: 'הוקצה',
          column__: idNumber,
          column_1: new Date(),
        })
        .execute();

      assignedCoupons.push({
        idNumber,
        couponCode: coupon.column_ || '',
      });
    }

    await registrationsBoard
      .item(data.registrationId)
      .update({
        column_6: 'אושר',
      })
      .execute();

    return { success: true, coupons: assignedCoupons };
  });

export const getCouponStats = createServerFn({ method: 'GET' })
  .validator(z.object({}).optional())
  .handler(async () => {
    const { קופוניםBoard } = await import('@api/BoardSDK');
    const board = new קופוניםBoard();

    const result = await board
      .aggregate()
      .groupBy('column')
      .countItems('count')
      .execute();

    return result || [];
  });
